"use client"

import styled from "@emotion/styled"
import { Swiper, SwiperSlide } from "swiper/react"
import { extractProperty, extractPropertyMediaItems, getUmbracoMediaUrl } from "utils"
import PortContainer from "./PortContainer"
import PortTitle from "./PortTitle"

import { Box } from "@mui/material"
import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import "swiper/css"

const StyledSection = styled.section<{ $darkMode?: boolean }>`
  --p-bg: ${(props) =>
    props.$darkMode
      ? props.theme.colors.port.darkBackgroundColor
      : props.theme.colors.port.lightBackgroundColor};
  --p-text: ${(props) =>
    props.$darkMode
      ? props.theme.colors.port.darkTextColor
      : props.theme.colors.port.lightTextColor};
  --p-border-color: ${(props) =>
    props.$darkMode
      ? props.theme.colors.port.darkBorderColor
      : props.theme.colors.port.lightBorderColor};
  --p-border-color-hover: ${(props) =>
    props.$darkMode
      ? props.theme.colors.port.darkBorderColor
      : props.theme.colors.port.defaultTextColor};
  --p-item-bg: ${(props) => (props.$darkMode ? "rgba(255, 255, 255, 0.05)" : "transparent")};
  --p-item-bg-hover: transparent;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 0;
  gap: 64px;
  background-color: var(--p-bg);
  color: var(--p-text);
  overflow: hidden;
`

const StyledContent = styled.div``

const StyledItemsWrapper = styled.div<{ $center?: boolean }>`
  display: flex;
  width: 100%;

  .swiper {
    padding-block: 0 !important;
  }

  .swiper,
  .swiper-wrapper {
    overflow: visible !important;
  }

  ${({ $center, theme }) =>
    $center &&
    `
    ${theme.breakpoints.up("md")} {
      .swiper-wrapper {
        justify-content: center;
      }
    }
  `}

  .swiper-slide {
    width: 260px;
    height: 320px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    padding: 16px;
    border-radius: 8px;
    border: 1px solid var(--p-border-color);
    background: var(--p-item-bg);
    text-align: center;
    &:hover {
      border-color: var(--p-border-color-hover);
      background: var(--p-item-bg-hover);
    }
  }
`

const ItemTitle = styled.h4`
  margin: 0;
  font-size: 24px;
  font-weight: 400;
  line-height: 150%; /* 36px */
  text-transform: capitalize;
`

const StyledItemIcon = styled(Image)`
  width: 20px;
  max-width: 20px;
  height: 20px;
  object-fit: contain;
  flex-basis: 100%;
`

const StyledPortName = styled.h6`
  margin: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 150%; /* 24px */
  letter-spacing: 0.48px;
  text-transform: capitalize;
`

const StyledTestimonialText = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
`

type ItemType = {
  customerName: string
  port: string
  testimonialText: string
  icon: string
}

type PortCustomerTestimonialsProps = {
  data?: any
}

const PortCustomerTestimonials = ({ data }: PortCustomerTestimonialsProps) => {
  const blackTitle = extractProperty(data, "blackTitle")
  const blueTitle = extractProperty(data, "blueTitle")
  const subtitle = extractProperty(data, "subtitle")
  const darkMode = extractProperty(data, "darkMode") || false
  const rightAligned = extractProperty(data, "textAlignment") ? "right" : "center"
  const itemsList = extractProperty(data, "itemsList")

  const items: ItemType[] =
    itemsList?.map((item: any) => ({
      customerName: extractProperty(item, "customerName"),
      port: extractProperty(item, "port"),
      testimonialText: extractProperty(item, "testimonialText"),
      icon: extractPropertyMediaItems(item, "icon")?.[0],
    })) || []

  const wrapperRef = useRef<HTMLDivElement>(null)
  const [shouldCenter, setShouldCenter] = useState(false)

  useEffect(() => {
    const SLIDE_WIDTH = 260
    const GAP = 24
    const check = () => {
      const el = wrapperRef.current?.querySelector(".swiper") as HTMLElement | null
      if (el) {
        const totalWidth = items.length * SLIDE_WIDTH + (items.length - 1) * GAP
        setShouldCenter(totalWidth <= el.clientWidth)
      }
    }
    check()
    window.addEventListener("resize", check)
    return () => window.removeEventListener("resize", check)
  }, [items.length])

  // in case data fields are empty, return null
  if (!blackTitle && !blueTitle && !subtitle && (!items || items.length === 0)) {
    return null
  }

  return (
    <StyledSection id="port-customer-testimonials" $darkMode={!!darkMode}>
      {(blackTitle || blueTitle || subtitle) && (
        <PortContainer paddingInline={20}>
          <StyledContent>
            <PortTitle
              normalText={blackTitle}
              highlightedText={blueTitle}
              centered
              overwrittenColor={darkMode ? "var(--p-text)" : undefined}
              titleSx={{
                direction: rightAligned === "right" ? "rtl" : "ltr",
                textAlign: { xs: rightAligned },
                fontSize: {
                  xs: "32px",
                  md: "60px",
                },
                lineHeight: {
                  xs: "125%",
                },
              }}
              subtitle={subtitle}
              subtitleSx={{
                direction: rightAligned === "right" ? "rtl" : "ltr",
                textAlign: { xs: rightAligned },
                fontSize: {
                  xs: "16px",
                  md: "24px",
                },
              }}
            />
          </StyledContent>
        </PortContainer>
      )}
      <PortContainer
        paddingInline={20}
        maxWidthXL={1440}
        sx={{
          alignSelf: "center",
        }}
      >
        {items && items.length > 0 && (
          <StyledItemsWrapper ref={wrapperRef} $center={shouldCenter}>
            <Swiper slidesPerView={"auto"} spaceBetween={24}>
              {items.map(({ customerName, port, testimonialText, icon }, index) => (
                <SwiperSlide key={`${customerName}-${index}`}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      textAlign: "center",
                      gap: "8px",
                    }}
                  >
                    {customerName && <ItemTitle>{customerName}</ItemTitle>}
                    {port && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                          gap: "8px",
                        }}
                      >
                        {icon && (
                          <StyledItemIcon
                            src={getUmbracoMediaUrl(icon)}
                            alt={customerName || `icon-${index}`}
                            style={{ filter: darkMode ? "brightness(0) invert(1)" : "none" }}
                            width={20}
                            height={20}
                          />
                        )}
                        <StyledPortName>{port}</StyledPortName>
                      </Box>
                    )}
                  </Box>
                  {testimonialText && (
                    <StyledTestimonialText>{testimonialText}</StyledTestimonialText>
                  )}
                </SwiperSlide>
              ))}
            </Swiper>
          </StyledItemsWrapper>
        )}
      </PortContainer>
    </StyledSection>
  )
}
export default PortCustomerTestimonials
